// src/pages/Donors.jsx
import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, TextField,
  Grid, Card, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, MenuItem,
  TablePagination, Avatar, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, Snackbar, Alert, CircularProgress
} from '@mui/material';
import {
  Download as DownloadIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import apiEndpoints from '../../apiconfig';

const donorColumns = [
  { id: 'id', label: 'S.No', minWidth: 70 },
  { id: 'name', label: 'Donor Name', minWidth: 180 },
  // { id: 'campaign', label: 'Campaign', minWidth: 180 }, // Add this new column
  { id: 'contact', label: 'Contact', minWidth: 150 },
  { id: 'location', label: 'Location', minWidth: 150 },
  { id: 'totalDonations', label: 'Last Donation (₹)', minWidth: 150,  align: 'right'  },
  { id: 'lastDonation', label: 'Last Donation Date', minWidth: 120, align: 'center' },
  { id: 'actions', label: 'Actions', minWidth: 100, align: 'center' },
];

// Generate mock donation history
// const generateDonationHistory = (id) => {
//   const count = Math.floor(Math.random() * 5) + 3; // 3-7 donations
//   return Array.from({ length: count }, (_, i) => ({
//     id: `${id}-${i + 1}`,
//     date: new Date(Date.now() - Math.floor(Math.random() * 365) * 86400000).toLocaleDateString(),
//     amount: Math.floor(Math.random() * 50000) + 1000,
//     campaign: `Campaign ${Math.floor(Math.random() * 10) + 1}`,
//     paymentMethod: ['UPI', 'Credit Card', 'Bank Transfer', 'Cheque'][Math.floor(Math.random() * 4)],
//     status: ['Completed', 'Pending', 'Failed'][Math.floor(Math.random() * 3)]
//   }));
// };

// const mockDonors = Array.from({ length: 50 }, (_, i) => {
//   const id = i + 1;
//   return {
//     id,
//     name: `Donor ${id}`,
//     avatar: `/avatars/${Math.floor(Math.random() * 5) + 1}.png`,
//     email: `donor${id}@example.com`,
//     phone: `+91 ${Math.floor(9000000000 + Math.random() * 1000000000)}`,
//     pan: `ABCDE${Math.floor(1000 + Math.random() * 9000)}F`,
//     city: ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Kolkata'][Math.floor(Math.random() * 5)],
//     state: ['Maharashtra', 'Delhi', 'Karnataka', 'Tamil Nadu', 'West Bengal'][Math.floor(Math.random() * 5)],
//     totalDonations: Math.floor(Math.random() * 100000) + 5000,
//     lastDonation: new Date(Date.now() - Math.floor(Math.random() * 90) * 86400000).toLocaleDateString(),
//     address: `${Math.floor(Math.random() * 100) + 1} Street, ${['East', 'West', 'North', 'South'][Math.floor(Math.random() * 4)]} ${['Park', 'Nagar', 'Area', 'Zone'][Math.floor(Math.random() * 4)]}`,
//     joinedDate: new Date(Date.now() - Math.floor(Math.random() * 365) * 86400000).toLocaleDateString(),
//     donationHistory: generateDonationHistory(id),
//     notes: `Donor ${id} has been supporting our causes since ${Math.floor(Math.random() * 5) + 2018}. They prefer ${['education', 'healthcare', 'environment', 'animal welfare'][Math.floor(Math.random() * 4)]} initiatives.`
//   };
// });

const Donors = () => {
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    location: '',
    minAmount: '',
    maxAmount: '',
  });
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const fetchDonors = async () => {
      try {
        const params = new URLSearchParams();
        if (searchText) params.append('search', searchText);
        if (filters.minAmount) params.append('minAmount', filters.minAmount);
        if (filters.maxAmount) params.append('maxAmount', filters.maxAmount);
        if (filters.location) params.append('search', filters.location);

        const response = await fetch(`${apiEndpoints.donate}?uniqueDonors=true${params.toString()}`);
        const data = await response.json();

        const transformedDonors = data.map((donor) => ({
          id: donor.id,
          name: donor.donar_name,
          campaign: donor.campaign || 'General Donation',
          avatar: `/avatars/${Math.floor(Math.random() * 5) + 1}.png`,
          email: donor.donar_email,
          phone: donor.mobile,
          pan: donor.pan_number,
          aadhar: donor.addhar_number,
          city: extractCityFromAddress(donor.address),
          totalDonations: parseFloat(donor.amount),
          lastDonation: formatDate(donor.lastdonationdate),
          address: donor.address,
          joinedDate: formatDate(donor.lastdonationdate),
          donationFrequency: donor.donateperiod,
          notes: `Donor supports various causes. PAN: ${donor.pan_number}.`
        }));

        console.log("data after fetch",transformedDonors);

        setDonors(transformedDonors);
      } catch (error) {
        console.error('Error fetching donors:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load donor data',
          severity: 'error'
        });
        setDonors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDonors();

  }, [searchText, filters.minAmount, filters.maxAmount, filters.location]);

  const fetchDonorById = async (id) => {
    try {
      const response = await fetch(`${apiEndpoints.donate}?id=${id}`);
      const data = await response.json();

      if (!data.profile_details) {
        throw new Error('Donor not found');
      }

      const profile = data.profile_details;
      const donations = data.donation_history.donations || [];

      return {
        id: profile.id,
        name: profile.donar_name,
        avatar: `/avatars/${Math.floor(Math.random() * 5) + 1}.png`,
        email: profile.donar_email,
        phone: profile.mobile,
        pan: profile.pan_number,
        aadhar: profile.aadhar_number,
        city: profile.address,
        totalDonations: parseFloat(profile.total_donated),
        lastDonation: formatDate(profile.lastdonationdate),
        address: profile.address,
        joinedDate: formatDate(profile.lastdonationdate),
        donationFrequency: profile.donateperiod,
        donationHistory: donations.map(donation => ({
          id: donation.id,
          date: formatDate(donation.lastdonationdate),
          amount: parseFloat(donation.amount),
          campaign: donation.campaign,
          paymentMethod: 'Online Payment',
          status: 'Completed'
        })),
        notes: `Donor supports various causes. PAN: ${profile.pan_number}. Contact: ${profile.mobile}.`
      };
    } catch (error) {
      console.error('Error fetching donor:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load donor details',
        severity: 'error'
      });
      return null;
    }
  };


  const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    // Try to parse as ISO format first
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
    
    // Try to parse as "21 Jun 2025" format
    if (typeof dateString === 'string' && dateString.match(/\d{1,2} \w{3} \d{4}/)) {
      return dateString; // Already in correct format
    }
    
    return 'N/A';
  } catch {
    return 'N/A';
  }
};
  const extractCityFromAddress = (address) => {
    if (!address) return 'Unknown';
    const parts = address.split(',');
    return parts.length > 1 ? parts[parts.length - 2].trim() : address;
  };

  const extractStateFromAddress = (address) => {
    if (!address) return 'Unknown';
    const parts = address.split(',');
    return parts.length > 0 ? parts[parts.length - 1].trim() : 'Unknown';
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSearch = (e) => setSearchText(e.target.value);

  const handleResetFilters = () => {
    setFilters({
      location: '',
      minAmount: '',
      maxAmount: '',
    });
    setSearchText('');
  };

  const [profileLoading, setProfileLoading] = useState(false);

  const fetchDonorDetails = async (id) => {
    setProfileLoading(true);
    const donor = await fetchDonorById(id);
    setSelectedDonor(donor);
    setProfileLoading(false);
  };

  const handleViewProfile = (donor) => {
    fetchDonorDetails(donor.id);
    setActiveTab(0);
    setOpenProfileDialog(true);
  };



  const handleCloseProfile = () => {
    setOpenProfileDialog(false);
    setSelectedDonor(null);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Export to CSV functionality
  const handleExport = () => {
    try {
      // Prepare CSV content
      const headers = ['ID', 'Name', 'Campaign', 'Email', 'Phone', 'PAN', 'City', 'State', 'Total Donations', 'Last Donation'];
      const csvContent = [
        headers.join(','),
        ...filteredDonors.map(donor =>
          [
            donor.id,
            `"${donor.name}"`,
            `"${donor.campaign}"`, // Add campaign to export
            donor.email,
            donor.phone,
            donor.pan,
            donor.city,
            donor.state,
            donor.totalDonations,
            donor.lastDonation,
          ].join(',')
        )
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `donors_export_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({
        open: true,
        message: 'Donor data exported successfully!',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to export donor data',
        severity: 'error'
      });
    }
  };

  const filteredDonors = donors.filter(donor => {
    return (
      (filters.location ? donor.city === filters.location : true) &&
      (filters.minAmount ? donor.totalDonations >= Number(filters.minAmount) : true) &&
      (filters.maxAmount ? donor.totalDonations <= Number(filters.maxAmount) : true) &&
      (searchText ? donor.name.toLowerCase().includes(searchText.toLowerCase()) ||
        donor.email.toLowerCase().includes(searchText.toLowerCase()) : true)
    );
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }
  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>Donors</Typography>

      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search donors..."
              value={searchText}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <SearchIcon color="action" />,
              }}
            />
          </Grid>

          {/* <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              select
              size="small"
              label="Location"
              name="location"
              value={filters.location}
              onChange={handleFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Mumbai">Mumbai</MenuItem>
              <MenuItem value="Delhi">Delhi</MenuItem>
              <MenuItem value="Bangalore">Bangalore</MenuItem>
              <MenuItem value="Chennai">Chennai</MenuItem>
              <MenuItem value="Kolkata">Kolkata</MenuItem>
            </TextField>
          </Grid> */}

          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Min Amount"
              name="minAmount"
              value={filters.minAmount}
              onChange={handleFilterChange}
              type="number"
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Max Amount"
              name="maxAmount"
              value={filters.maxAmount}
              onChange={handleFilterChange}
              type="number"
            />
          </Grid>

          <Grid item xs={6} md={2} display="flex" gap={1}>
            <Button
              variant="outlined"
              color="success"
              startIcon={<FilterListIcon />}
              onClick={handleResetFilters}
              sx={{ flex: 1 }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              sx={{ flex: 1 }}
            >
              Export
            </Button>
          </Grid>
        </Grid>
      </Card>

      <Card>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {donorColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    style={{ minWidth: column.minWidth, fontWeight: 600 }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDonors
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id}
                    onClick={() => handleViewProfile(row)}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    {donorColumns.map((column) => {
                      const value = row[column.id];

                      if (column.id === 'id') {
                        // Show serial number based on pagination
                        const serialNumber = page * rowsPerPage + filteredDonors.findIndex(d => d.id === row.id) + 1;
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            sx={{ color: 'success.main', fontWeight: 500 }}
                          >
                            {serialNumber}
                          </TableCell>
                        );
                      }

                      if (column.id === 'name') {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            <Box display="flex" alignItems="center">
                              <Avatar src={row.avatar} sx={{ width: 36, height: 36, mr: 2 }} />
                              {value}
                            </Box>
                          </TableCell>
                        );
                      }

                      // if (column.id === 'campaign') {
                      //   return (
                      //     <TableCell key={column.id} align={column.align}>
                      //       <Typography variant="body2">{row.campaign}</Typography>
                      //     </TableCell>
                      //   );
                      // }

                      if (column.id === 'contact') {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            <Box>
                              <Typography variant="body2">{row.email}</Typography>
                              <Typography variant="body2" color="text.secondary">{row.phone}</Typography>
                            </Box>
                          </TableCell>
                        );
                      }

                      if (column.id === 'location') {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            <Box>
                              <Typography variant="body2">{row.city}</Typography>
                              <Typography variant="body2" color="text.secondary">{row.state}</Typography>
                            </Box>
                          </TableCell>
                        );
                      }

                        if (column.id === 'totalDonations') {
                        return (
                          <TableCell key={column.id} align="right">
                          <Typography fontWeight={600}>₹{value.toLocaleString()}</Typography>
                          </TableCell>
                        );
                        }

                      if (column.id === 'actions') {
                        return (
                          <TableCell
                            key={column.id}
                            align="center"
                            onClick={(e) => e.stopPropagation()} // Prevent row click from triggering
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleViewProfile(row)}
                              color="primary"
                            >
                              <VisibilityIcon fontSize="small" color="success" />
                            </IconButton>
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell key={column.id} align={column.align}>
                          {value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredDonors.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Donor Profile Dialog */}
      <Dialog
        open={openProfileDialog}
        onClose={handleCloseProfile}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedDonor?.loading ? 'Loading...' : `Donor Profile`}
            </Typography>
            <IconButton onClick={handleCloseProfile}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>

          {profileLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedDonor && (
            <Box>
              <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
                <Tab label="Profile Details" />
                <Tab label="Donation History" />
                <Tab label="Notes" />
              </Tabs>

              {activeTab === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Avatar
                      src={selectedDonor.avatar}
                      sx={{ width: 120, height: 120, mx: 'auto' }}
                    />
                  </Grid>

                  <Grid item xs={12} md={9}>
                    <Typography variant="h5" gutterBottom>
                      {selectedDonor.name}
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                        <Typography>{selectedDonor.email}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
                        <Typography>{selectedDonor.phone}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">PAN Number</Typography>
                        <Typography>{selectedDonor.pan}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">Aadhar Number</Typography>
                        <Typography>{selectedDonor.aadhar || 'Not provided'}</Typography>
                      </Grid>

                      {/* <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">Location</Typography>
                        <Typography>
                          {selectedDonor.city}, {selectedDonor.state}
                        </Typography>
                      </Grid> */}

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">Address</Typography>
                        <Typography>{selectedDonor.address}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">Total Donations</Typography>
                        <Typography variant="h6" color="primary">
                          ₹{selectedDonor.totalDonations.toLocaleString()}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">Last Donation</Typography>
                        <Typography>{selectedDonor.lastDonation}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">Member Since</Typography>
                        <Typography>{selectedDonor.joinedDate}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {activeTab === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Donation History ({selectedDonor.donationHistory.length})
                  </Typography>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Campaign</TableCell>
                          <TableCell align="right">Amount (₹)</TableCell>
                          <TableCell>Payment Method</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedDonor.donationHistory.map((donation) => (
                          <TableRow key={donation.id}>
                            <TableCell>{donation.date}</TableCell>
                            <TableCell>{donation.campaign || donation.cause_name}</TableCell>
                            <TableCell align="right">{donation.amount.toLocaleString()}</TableCell>
                            <TableCell>{donation.paymentMethod}</TableCell>
                            <TableCell>
                              {donation.status}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {activeTab === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Notes
                  </Typography>
                  <Typography>
                    {selectedDonor.notes}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseProfile} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Donors;